import React, { useRef, useState } from "react";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PropTypes from "prop-types";

/**
 * Split button based on {@link https://v4.mui.com/components/button-group/#split-button Mui Split Button}
 */
export const SplitButton = ({
  options,
  changeOnSelect = false,
  showSelectedInList = false,
  variant = "contained",
  color = "primary",
  menuPlacement = "bottom-end",
  ...buttonProps
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState(options && options[0]);

  const handleMenuItemClick = option => {
    if (changeOnSelect) {
      setSelectedOption(option);
    }
    option.onClick();
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        aria-label="split button"
        variant={variant}
        color={color}
        {...buttonProps}
      >
        <Button
          onClick={selectedOption?.onClick}
          disabled={selectedOption?.disabled}
        >
          {selectedOption?.title}
        </Button>
        <Button
          size="small"
          aria-label="toggle options menu"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement={menuPlacement}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {options
                    .filter(
                      option =>
                        showSelectedInList || option.id !== selectedOption.id
                    )
                    .map(option => {
                      const { id, title, disabled } = option;
                      return (
                        <MenuItem
                          key={id}
                          disabled={disabled}
                          selected={id === selectedOption.id}
                          onClick={() =>
                            !disabled && handleMenuItemClick(option)
                          }
                        >
                          {title}
                        </MenuItem>
                      );
                    })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

SplitButton.propTypes = {
  /* options containing the title and an onClick function to call */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /* whether the button should change when an option is selected */
  changeOnSelect: PropTypes.bool,
  /* Whether the currently selected button should show in the list*/
  showSelectedInList: PropTypes.bool,
  /* the variant to use*/
  variant: PropTypes.oneOf(["outlined", "contained", "text"]),
  /* The color of the split button, supports the theme colors*/
  color: PropTypes.oneOf(["default", "inherit", "primary", "secondary"]),
  /* Where the menu will appear relative to the button */
  menuPlacement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top"
  ])
};
